// MyCalendar — donor-portal cross-org calendar (comp-ε GAP-3a,
// CHG-2403 / ITER-905145). v2-canary parity port of the v1 page
// (web/dashboard/src/pages/MyCalendar.tsx, CHG-1943).
//
// Route /my-giving/calendar. Consumes the comp-ε donor calendar API
// (donor_portal_calendar.py): a month-bucketed agenda of upcoming events /
// pledge due dates / recurring donations / cultural observances across every
// org the donor supports, plus a non-form Subscribe modal that surfaces the
// donor's personal iCal feed URL (lazy-allocated server-side) with copy +
// regenerate.
//
// Spec: specs/tier-05-experience/my-giving-calendar.cross-screen.yaml.
//
// Composition: hand-rolled custom agenda page, following the established v2
// donor-portal convention (MyImpact / MyRecurring / MyPledges are likewise
// hand-rolled — an agenda is not a tabular ListPage). PageHeader for the
// header, useFormatters for dates, CSS vars for all color/surface, and the
// existing ModalShell non-form overlay for the subscribe modal (so rules 2/11
// re: forms-in-modals do not apply — this modal has no form fields).

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calendar, Loader2, RefreshCw, Copy, Check } from "lucide-react";
import { PageHeader } from "../components/composition/PageHeader";
import { ModalShell } from "../components/role/ModalShell";
import { useFormatters } from "../contexts/IntlContext";
import {
  meCalendarApi,
  type DonorCalendarEvent,
  type DonorCalendarResponse,
} from "../api/me-calendar";

function relativeLabel(
  days: number,
  formatDate: (d: string, style?: "short" | "medium" | "long" | "relative") => string,
  dateStr: string,
): string {
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days < 0) {
    const absDays = Math.abs(days);
    return `${absDays} day${absDays === 1 ? "" : "s"} ago`;
  }
  if (days < 7) return `In ${days} day${days === 1 ? "" : "s"}`;
  return formatDate(dateStr, "medium");
}

function eventTypeLabel(type: string): string {
  switch (type) {
    case "religious":
      return "Cultural";
    case "event":
      return "Event";
    case "campaign":
      return "Campaign";
    case "campaign_deadline":
      return "Deadline";
    case "pledge_due":
      return "Pledge";
    case "recurring":
      return "Recurring";
    default:
      return type;
  }
}

function SubscribeCalendarModal({ onClose }: { onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["me", "calendar", "feed-url"] as const,
    queryFn: meCalendarApi.fetchFeedUrl,
  });
  const [regenerating, setRegenerating] = useState(false);

  const url = data?.data?.feed_url ?? "";
  const instructions = data?.data?.instructions;

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable (insecure context / denied) — the URL is
      // selectable in the field, so this is a non-fatal best-effort.
    }
  };

  const regenerate = async () => {
    // CHG-2938 / Slice J (DP-R9) — regeneration invalidates the old URL,
    // which silently breaks every calendar app still subscribed to it, so
    // it must be confirmed (window.confirm matches MyPledgeDetail /
    // MyRecurringDetail precedent).
    if (
      !window.confirm(
        "Regenerate your calendar URL? Your existing calendar subscriptions " +
          "(Google, Outlook, Apple) will stop updating, and you'll need to " +
          "re-add the new URL in each calendar app.",
      )
    ) {
      return;
    }
    setRegenerating(true);
    try {
      // Rotate the token, then refetch /feed-url so the displayed URL AND
      // the per-platform instruction lines (which embed the URL) both pick
      // up the new token — regenerate's response carries no instructions.
      await meCalendarApi.regenerateFeedUrl();
      await refetch();
      setCopied(false);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <ModalShell title="Subscribe to my calendar" onClose={onClose} size="md">
      <div
        style={{
          padding: "16px 20px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: 24,
              color: "var(--color-text-muted)",
            }}
          >
            <Loader2 size={20} className="animate-spin" aria-label="Loading" />
          </div>
        ) : error ? (
          <p style={{ fontSize: 14, color: "var(--color-error)" }}>
            Could not load your subscription URL. Please close and try again.
          </p>
        ) : (
          <>
            <p style={{ fontSize: 13, color: "var(--color-text-muted)", margin: 0 }}>
              Add this URL to Google Calendar, Outlook, or Apple Calendar to keep
              your events, pledge due dates, and recurring donations in sync.
            </p>
            <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
              <input
                type="text"
                readOnly
                value={url}
                aria-label="Calendar feed URL"
                data-testid="calendar-feed-url"
                onFocus={(e) => e.currentTarget.select()}
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 12,
                  fontFamily: "var(--font-mono, ui-monospace, monospace)",
                  padding: "8px 10px",
                  borderRadius: "var(--radius-sm, 6px)",
                  border: "1px solid var(--color-border)",
                  background: "var(--color-bg-subtle)",
                  color: "var(--color-text)",
                }}
              />
              <button
                type="button"
                onClick={copy}
                className="btn-secondary"
                aria-label="Copy feed URL"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  padding: "0 12px",
                  borderRadius: "var(--radius-sm, 6px)",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            {instructions ? (
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  fontSize: 12,
                  color: "var(--color-text-muted)",
                }}
              >
                <li>{instructions.google}</li>
                <li>{instructions.outlook}</li>
                <li>{instructions.apple}</li>
              </ul>
            ) : null}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderTop: "1px solid var(--color-border-subtle)",
                paddingTop: 12,
              }}
            >
              <span style={{ fontSize: 12, color: "var(--color-text-subtle)" }}>
                Lost control of this URL? Regenerate to revoke it.
              </span>
              <button
                type="button"
                onClick={regenerate}
                disabled={regenerating}
                className="btn-secondary"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  padding: "6px 12px",
                  borderRadius: "var(--radius-sm, 6px)",
                  cursor: regenerating ? "default" : "pointer",
                  opacity: regenerating ? 0.6 : 1,
                }}
              >
                <RefreshCw
                  size={14}
                  className={regenerating ? "animate-spin" : undefined}
                />
                Regenerate
              </button>
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
}

export function MyCalendar() {
  const { formatDate } = useFormatters();
  const [showSubscribe, setShowSubscribe] = useState(false);

  const { data, isLoading, error, refetch, isFetching } =
    useQuery<DonorCalendarResponse>({
      queryKey: ["me", "calendar", "events"] as const,
      queryFn: () => meCalendarApi.fetchCalendarEvents(90),
      refetchInterval: 300_000,
    });

  const events = data?.data?.events ?? [];
  // Bucket by YYYY-MM, preserving the backend's sorted-by-date order.
  const buckets: Record<string, DonorCalendarEvent[]> = {};
  for (const ev of events) {
    const key = (ev.date || "").slice(0, 7) || "unknown";
    (buckets[key] ??= []).push(ev);
  }
  const bucketKeys = Object.keys(buckets).sort();

  return (
    <main style={{ maxWidth: 880, margin: "0 auto", padding: "16px 24px 48px" }}>
      <PageHeader
        title="My calendar"
        subtitle="Your upcoming events, pledges, and recurring donations across every organization you support."
        actions={[
          {
            id: "subscribe",
            label: "Subscribe",
            onClick: () => setShowSubscribe(true),
            icon: <Calendar size={16} />,
            variant: "primary",
          },
        ]}
      />

      {isLoading ? (
        <div
          className="card"
          style={{
            padding: 32,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Loader2
            size={24}
            className="animate-spin"
            style={{ color: "var(--color-text-subtle)" }}
            aria-label="Loading calendar"
          />
        </div>
      ) : error ? (
        <div className="card" style={{ padding: 24 }}>
          <p style={{ fontSize: 14, color: "var(--color-error)", marginTop: 0 }}>
            Could not load your calendar. Please try again.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="btn-secondary"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              padding: "6px 12px",
              borderRadius: "var(--radius-sm, 6px)",
              cursor: "pointer",
            }}
          >
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      ) : events.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: "center" }}>
          <Calendar
            size={36}
            aria-hidden="true"
            style={{
              color: "var(--color-text-subtle)",
              margin: "0 auto 12px",
              display: "block",
            }}
          />
          <p
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: "var(--color-text)",
              margin: "0 0 4px",
            }}
          >
            No upcoming events
          </p>
          <p
            style={{
              fontSize: 12,
              color: "var(--color-text-muted)",
              margin: "0 0 16px",
            }}
          >
            We'll show your pledge due dates, recurring donations, and cultural
            events from every organization you support as they come up.
          </p>
          <button
            type="button"
            onClick={() => setShowSubscribe(true)}
            className="btn-secondary"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              padding: "6px 12px",
              borderRadius: "var(--radius-sm, 6px)",
              cursor: "pointer",
            }}
          >
            <Calendar size={14} /> Subscribe to my calendar
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {bucketKeys.map((bucketKey) => {
            const bucketEvents = buckets[bucketKey];
            const firstEvent = bucketEvents[0];
            const monthLabel = firstEvent
              ? formatDate(firstEvent.date, "long").replace(/\b\d{1,2}\b,?\s?/, "")
              : bucketKey;
            return (
              <section
                key={bucketKey}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                <h2
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "var(--color-text-muted)",
                    margin: 0,
                  }}
                >
                  {monthLabel}
                </h2>
                <ul
                  style={{
                    listStyle: "none",
                    margin: 0,
                    padding: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  {bucketEvents.map((ev) => (
                    <li
                      key={ev.id}
                      className="card"
                      data-testid="my-calendar-event"
                      data-event-type={ev.type}
                      style={{
                        padding: 16,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          marginTop: 5,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          flexShrink: 0,
                          background: ev.color || "var(--color-primary)",
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <p
                            style={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: "var(--color-text)",
                              margin: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {ev.title}
                          </p>
                          <span
                            style={{
                              fontSize: 12,
                              color: "var(--color-text-subtle)",
                              flexShrink: 0,
                            }}
                          >
                            {relativeLabel(ev.days_until, formatDate, ev.date)}
                          </span>
                        </div>
                        <p
                          style={{
                            fontSize: 12,
                            color: "var(--color-text-muted)",
                            margin: "4px 0 0",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              padding: "1px 6px",
                              borderRadius: 999,
                              background: "var(--color-bg-subtle)",
                              marginRight: 8,
                              fontSize: 10,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            {eventTypeLabel(ev.type)}
                          </span>
                          {ev.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
          {isFetching ? (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 8 }}>
              <Loader2
                size={16}
                className="animate-spin"
                style={{ color: "var(--color-text-subtle)" }}
                aria-label="Refreshing"
              />
            </div>
          ) : null}
        </div>
      )}

      {showSubscribe ? (
        <SubscribeCalendarModal onClose={() => setShowSubscribe(false)} />
      ) : null}
    </main>
  );
}
